/**
 * <ChildChips> — la rangée des enfants de la famille (molecule).
 *
 * Elle ne s'affiche qu'à partir de deux enfants : à un seul, il n'y a rien à choisir (règle du
 * legacy `ChildChips.js:17`, reprise telle quelle). Centrée — son défaut —, la rangée tient au
 * milieu tant qu'elle rentre dans la largeur et défile dès qu'elle déborde ; c'est ce que fait
 * `<HorizontalList center>`.
 *
 * `center={false}` la laisse commencer à gauche. C'est ce qu'il faut sur la bande d'un téléphone :
 * une fratrie de quatre y déborde, et une rangée centrée qui déborde sort **des deux côtés** —
 * elle cache le premier enfant autant que le dernier, et le premier est celui dont l'écran parle.
 *
 * `<ParentHeader>` la pose au centre du header au-delà de 800 px, en bande dessous en dessous ;
 * la rangée, elle, ne sait rien de l'endroit où elle est montée. C'est `hasChildChips()` qui dit
 * à l'appelant s'il y a une bande à dessiner, pour que la règle des deux enfants ne s'écrive
 * qu'ici.
 */
import React from "react"

import { type UIParentChild } from "../../../ports/primary/ui/types"
import { HorizontalList } from "../../atoms/HorizontalList"
import { ChildChip } from "../ChildChip"

/** Y a-t-il quelque chose à choisir ? En dessous de deux enfants, non. */
export function hasChildChips(childProfiles: UIParentChild[]): boolean {
    return childProfiles.length >= 2
}

export type ChildChipsProps = {
    /** Les enfants de la famille, dans l'ordre d'affichage. */
    childProfiles: UIParentChild[]
    /** Enfant dont l'écran parle. */
    selectedChildId?: string | number
    /**
     * Enfant vers lequel la bascule de compte est en cours : sa chip est la sélectionnée dès le
     * geste, et la rangée ne répond plus le temps de la bascule.
     */
    switchingChildId?: string | number | null
    onSelectChild?: (child: UIParentChild) => void
    /** Rangée centrée dans sa largeur — défaut `true`. `false` la colle à gauche. */
    center?: boolean
    testID?: string
}

export function ChildChips(props: ChildChipsProps) {
    const { childProfiles, selectedChildId, switchingChildId = null, onSelectChild, center = true, testID } = props

    if (!hasChildChips(childProfiles)) return null

    const switching = switchingChildId != null
    // La chip touchée répond tout de suite : c'est elle, la sélectionnée, sans attendre le compte.
    const shownChildId = switchingChildId ?? selectedChildId

    return (
        <HorizontalList gap="2" padding="2" center={center} testID={testID}>
            {childProfiles.map((child) => (
                <ChildChip
                    key={child.id}
                    child={child}
                    selected={child.id === shownChildId}
                    disabled={switching}
                    onPress={onSelectChild}
                />
            ))}
        </HorizontalList>
    )
}
